import { Box } from "@mui/material";
import { useCallback, useState } from "react";
import { useAppSelector } from "src/redux/hooks/redux";
import { useModals } from "../../../context/ModalContext";
import { useGlobalSnackbar } from "../../../context/SnackbarContext";
import { selectRole } from "src/redux/features/user/selectors/userState.selectors";
import { MessageGroupView } from "../MessageGroupView";
import { useMessageGroupAdminMutations } from "../hooks/useMessageGroupAdminMutations";
import { useMessageGroupSummaryQuery } from "../hooks/useMessageGroupSummaryQuery";
import { isEmployeeRole } from "../utils/messageGroupDisplay.utils";
import { AddMembersModal } from "../modals/AddMembersModal";
import { PickableMember } from "../modals/MemberPicker";
import { GroupMembersPanel } from "../modals/GroupMembersPanel";
import { RenameGroupModal } from "../modals/RenameGroupModal";
import { MessageGroupHeader } from "./MessageGroupHeader";

interface GroupChatWrapperProps {
  groupUid: string;
  companyId?: number | string | null;
  fallbackName: string;
  isFocused?: boolean;
  focusMessageUid?: string | null;
  focusMessageText?: string | null;
  focusMessageRequestKey?: string | null;
  onFocusMessageConsumed?: () => void;
  onClosed?: () => void;
}

export const GroupChatWrapper = ({
  groupUid,
  companyId,
  fallbackName,
  isFocused = true,
  focusMessageUid,
  focusMessageText,
  focusMessageRequestKey,
  onFocusMessageConsumed,
  onClosed,
}: GroupChatWrapperProps) => {
  const currentRole = useAppSelector(selectRole);
  const { data: group } = useMessageGroupSummaryQuery({ groupUid, companyId });
  const { renameGroup, addMembers, removeMember, leaveGroup, deleteGroup } = useMessageGroupAdminMutations(groupUid, companyId);
  const { showDialog } = useModals();
  const { showSnackbar } = useGlobalSnackbar();

  const reportError = useCallback(
    (error: unknown, fallback: string) => {
      const message = (error as any)?.response?.data?.message ?? (error as Error)?.message ?? fallback;
      showSnackbar(message, "error");
    },
    [showSnackbar],
  );

  const [membersOpen, setMembersOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  const canManage = !isEmployeeRole(currentRole) && Boolean(group?.isAdmin);

  const readError = (mutation: any) =>
    (mutation.error as any)?.response?.data?.message ?? (mutation.error as Error)?.message ?? null;

  const handleRename = useCallback(
    async (payload: { name: string; avatar: string | null; description: string | null }) => {
      await renameGroup.mutateAsync(payload);
      setRenameOpen(false);
    },
    [renameGroup],
  );

  const handleAddMembers = useCallback(
    async (members: PickableMember[]) => {
      await addMembers.mutateAsync(
        members.map((member) => ({
          type: member.type,
          uid: member.uid,
          ...(member.phoneNumber ? { phoneNumber: member.phoneNumber } : {}),
          ...(member.displayName ? { displayName: member.displayName } : {}),
        })),
      );
      setAddOpen(false);
    },
    [addMembers],
  );

  const groupLabel = group?.name ?? fallbackName;
  const isGroupText = group?.channel === "sms";

  const handleLeave = useCallback(() => {
    showDialog({
      type: "warning",
      title: "Leave this group?",
      message: isGroupText
        ? `Your number will be removed from "${groupLabel}" and you will stop receiving its messages. The other members keep the conversation.`
        : `You will be removed from "${groupLabel}" and will stop receiving its messages.`,
      confirmLabel: "Yes, leave",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await leaveGroup.mutateAsync();
          onClosed?.();
        } catch (error) {
          reportError(error, "Could not leave this group");
        }
      },
    });
  }, [leaveGroup, onClosed, showDialog, groupLabel, isGroupText, reportError]);

  const handleDelete = useCallback(() => {
    showDialog({
      type: "warning",
      title: "Delete this group?",
      message: isGroupText
        ? `"${groupLabel}" will be deleted for every company in it and the conversation will be closed. This cannot be undone.`
        : `"${groupLabel}" will be deleted for everyone. This cannot be undone.`,
      confirmLabel: "Yes, delete",
      cancelLabel: "Cancel",
      onConfirm: async () => {
        try {
          await deleteGroup.mutateAsync();
          onClosed?.();
        } catch (error) {
          reportError(error, "Could not delete this group");
        }
      },
    });
  }, [deleteGroup, onClosed, showDialog, groupLabel, isGroupText, reportError]);

  const handleRemoveMember = useCallback(
    (member: { type: PickableMember["type"]; uid: string; name?: string }) => {
      showDialog({
        type: "warning",
        title: "Remove this member?",
        message: `${member.name || member.uid} will be removed from "${groupLabel}" and will stop receiving its messages.`,
        confirmLabel: "Yes, remove",
        cancelLabel: "Cancel",
        onConfirm: async () => {
          try {
            await removeMember.mutateAsync({ type: member.type, uid: member.uid });
          } catch (error) {
            reportError(error, "Could not remove this member");
          }
        },
      });
    },
    [removeMember, showDialog, groupLabel, reportError],
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", height: "100%", overflow: "hidden" }}>
      <MessageGroupHeader
        group={group}
        fallbackName={fallbackName}
        canManage={canManage}
        membersOpen={membersOpen}
        onOpenMembers={() => setMembersOpen((current) => !current)}
        onRename={() => setRenameOpen(true)}
        onAddMembers={() => setAddOpen(true)}
        onLeave={handleLeave}
        onDelete={handleDelete}
      />

      <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
        <MessageGroupView
          groupUid={groupUid}
          companyId={companyId}
          isFocused={isFocused}
          focusMessageUid={focusMessageUid}
          focusMessageText={focusMessageText}
          focusMessageRequestKey={focusMessageRequestKey}
          onFocusMessageConsumed={onFocusMessageConsumed}
        />

        <GroupMembersPanel
          open={membersOpen}
          group={group}
          canManage={canManage}
          isRemoving={removeMember.isPending}
          onClose={() => setMembersOpen(false)}
          onAddMembers={() => setAddOpen(true)}
          onRemoveMember={handleRemoveMember}
        />
      </Box>

      <RenameGroupModal
        open={renameOpen}
        group={group}
        isSaving={renameGroup.isPending}
        errorMessage={readError(renameGroup)}
        onClose={() => setRenameOpen(false)}
        onConfirm={handleRename}
      />

      <AddMembersModal
        companyId={companyId}
        open={addOpen}
        group={group}
        isSaving={addMembers.isPending}
        errorMessage={readError(addMembers)}
        onClose={() => setAddOpen(false)}
        onConfirm={handleAddMembers}
      />
    </Box>
  );
};
